import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

import { useRelationshipResponsibleUsers } from 'services/repositories/relationships/relationships';
import { PartnerV2, RelationshipV2, UserDTO } from 'support/types';
import { BadgeList } from 'components/BadgeList/BadgeList';
import { EditRecipientsModal } from 'components/EditRecipientsModal/EditRecipientsModal';
import { NotificationResolving } from 'components/EditRecipientsModal/hooks/useUpdateRecipients';
import { Button } from 'components/Form/Button/Button';
import { NotificationRoutingBadge } from 'components/NotificationRoutingBadge/NotificationRoutingBadge';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';
import { Translate } from 'components/Translate/Translate';

type NotificationsManagementTabProps = {
  relationship: RelationshipV2;
  currentPartnerType?: string;
  currentPartnerId: string;
};

type EditingPartner = {
  id: string;
  name: string;
  emails: Array<string>;
  notificationResolving: NotificationResolving;
  showNotificationResolving: boolean;
};

export const NotificationsManagementTab = ({
  relationship,
  currentPartnerType,
  currentPartnerId,
}: NotificationsManagementTabProps) => {
  const { t } = useTranslation();
  const { data, isLoading } = useRelationshipResponsibleUsers({
    variables: { relationshipId: relationship.id },
  });
  const [editingPartner, setEditingPartner] = useState<EditingPartner | null>(null);

  if (isLoading) {
    return (
      <div className="flex animate-pulse flex-col gap-6">
        <div className="h-4 w-48 rounded-sm bg-gray-200" />
        <div className="h-4 w-64 rounded-sm bg-gray-200" />
        <div className="h-4 w-48 rounded-sm bg-gray-200" />
        <div className="h-4 w-64 rounded-sm bg-gray-200" />
      </div>
    );
  }

  const senderUsers = data?.sender_partner ?? [];
  const receiverUsers = data?.receiver_partner ?? [];
  const senderNotificationResolving = data?.sender_notification_resolving ?? 'default_configuration';
  const receiverNotificationResolving = data?.receiver_notification_resolving ?? 'default_configuration';

  const isSenderCurrentPartner = relationship.sender_partner.id === currentPartnerId;

  const senderPartnerType = relationship.sender_partner.partner_type ?? undefined;
  const receiverPartnerType = relationship.receiver_partner.partner_type ?? undefined;

  const showSenderEditButton = isSenderCurrentPartner
    ? true
    : canEditOtherPartner(currentPartnerType, senderPartnerType);

  const showReceiverEditButton = !isSenderCurrentPartner
    ? true
    : canEditOtherPartner(currentPartnerType, receiverPartnerType);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-bold text-black">
          {t('connections:relationshipDetails.notificationsManagement.title')}
        </h2>
        <p className="text-sm text-gray-500">{t('connections:relationshipDetails.notificationsManagement.subtitle')}</p>
        <p className="text-sm text-gray-500">
          <Translate i18nKey="connections:relationshipDetails.notificationsManagement.docsLink" />
        </p>
      </div>

      <div className="divide-y divide-gray-200 *:py-6 *:first:pt-0 *:last:pb-0">
        <PartnerSection
          partner={relationship.sender_partner}
          users={senderUsers}
          showEditButton={showSenderEditButton}
          showManagedByPartnerHint={!isSenderCurrentPartner && !showSenderEditButton}
          notificationResolving={senderNotificationResolving}
          showNotificationResolving={false}
          onEditClick={() =>
            setEditingPartner({
              id: relationship.sender_partner.id,
              name: relationship.sender_partner.name,
              emails: senderUsers.map((u) => u.email),
              notificationResolving: senderNotificationResolving,
              showNotificationResolving: false,
            })
          }
        />
        <PartnerSection
          partner={relationship.receiver_partner}
          users={receiverUsers}
          showEditButton={showReceiverEditButton}
          showManagedByPartnerHint={isSenderCurrentPartner && !showReceiverEditButton}
          notificationResolving={receiverNotificationResolving}
          showNotificationResolving
          onEditClick={() =>
            setEditingPartner({
              id: relationship.receiver_partner.id,
              name: relationship.receiver_partner.name,
              emails: receiverUsers.map((u) => u.email),
              notificationResolving: receiverNotificationResolving,
              showNotificationResolving: true,
            })
          }
        />
      </div>

      {editingPartner && (
        <EditRecipientsModal
          open
          onClose={() => setEditingPartner(null)}
          title={t('connections:relationshipDetails.notificationsManagement.modal.title', {
            partnerName: editingPartner.name,
          })}
          subtitle={t('connections:relationshipDetails.notificationsManagement.modal.subtitle', {
            partnerName: editingPartner.name,
          })}
          currentEmails={editingPartner.emails}
          currentNotificationResolving={editingPartner.notificationResolving}
          showNotificationResolving={editingPartner.showNotificationResolving}
          partnerName={editingPartner.name}
          config={{
            type: 'relationship',
            relationshipId: relationship.id,
            partnerId: editingPartner.id,
          }}
        />
      )}
    </div>
  );
};

const canEditOtherPartner = (currentPartnerType?: string, otherPartnerType?: string) => {
  return currentPartnerType === 'PROCUROS_CUSTOMER' && otherPartnerType !== 'PROCUROS_CUSTOMER';
};

const PartnerSection = ({
  partner,
  users,
  showEditButton,
  showManagedByPartnerHint,
  notificationResolving,
  showNotificationResolving,
  onEditClick,
}: {
  partner: PartnerV2;
  users: Array<UserDTO>;
  showEditButton: boolean;
  showManagedByPartnerHint: boolean;
  notificationResolving: NotificationResolving;
  showNotificationResolving: boolean;
  onEditClick: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2">
          <PartnerLogo imageUrl={partner.logo_url} size="small" />
          <h3 className="text-sm font-semibold text-gray-900">{partner.name}</h3>
        </div>
        <div className="mt-2">
          <BadgeList
            values={users.map((user) => user.email)}
            emptyLabel={t('connections:relationshipDetails.notificationsManagement.noRecipients')}
          />
        </div>
        {showNotificationResolving && (
          <NotificationRoutingBadge notificationResolving={notificationResolving} className="mt-2" />
        )}
      </div>
      {showEditButton && (
        <Button
          variant="secondary"
          onClick={onEditClick}
          analyticsId="notifications-management-tab:edit"
          LeftIcon={PencilSquareIcon}
        >
          {t('common:edit')}
        </Button>
      )}
      {showManagedByPartnerHint && (
        <span className="text-sm text-gray-400">
          {t('connections:relationshipDetails.notificationsManagement.managedByPartner')}
        </span>
      )}
    </div>
  );
};
